import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Form, FormGroup, Label, Input, Spinner, Button } from 'reactstrap';
import { BiCheckCircle, BiErrorCircle, BiLockAlt } from 'react-icons/bi';
import debounce from 'lodash/debounce';
import DatePicker from '../utility/datePicker';
import { QUOTE_STATUS_EDIT_OPTIONS } from '../../utils/quoteStatuses';
import { currencySymbol } from '../../constants/currencies';
import useUpdateQuote from '../../hooks/useUpdateQuote';

// Fields this card edits (i.e. that flow through the debounced autosave).
// CustomerId deliberately absent — see UpdateQuoteViewModel.cs.
const FIELDS = [
    'quoteNumber', 'status', 'title', 'description',
    'quotedAt', 'expiryDate',
    'isVatInclusive', 'discountType', 'discountValue',
    'siteAddress1', 'siteAddress2', 'siteTownCity', 'sitePostcode',
    'notes', 'internalNotes',
];

// DiscountType enum values match the backend: 0 None | 1 Amount | 2 Percent.
const DISCOUNT_TYPES = [
    { value: 0, label: 'None' },
    { value: 1, label: 'Fixed amount' },
    { value: 2, label: 'Percentage' },
];

function toDraft(quote) {
    return {
        quoteNumber:     quote?.quoteNumber     ?? '',
        status:          quote?.status          ?? 'Draft',
        title:           quote?.title           ?? '',
        description:     quote?.description     ?? '',
        quotedAt:        quote?.quotedAt        ?? '',
        expiryDate:      quote?.expiryDate      ?? '',
        isVatInclusive:  quote?.isVatInclusive  ?? true,
        discountType:    quote?.discountType    ?? 0,
        discountValue:   quote?.discountValue   ?? 0,
        siteAddress1:    quote?.siteAddress1    ?? '',
        siteAddress2:    quote?.siteAddress2    ?? '',
        siteTownCity:    quote?.siteTownCity     ?? '',
        sitePostcode:    quote?.sitePostcode     ?? '',
        notes:           quote?.notes           ?? '',
        internalNotes:   quote?.internalNotes   ?? '',
    };
}

function draftsEqual(a, b) {
    if (!a || !b) return a === b;
    return FIELDS.every(f => a[f] === b[f]);
}

// Shape the draft for the server. Empty YYYY-MM-DD strings → null so
// backend DateOnly? parses; empty notes → null so we don't persist empty
// strings that then render as a blank Terms block on the PDF.
function toBody(draft) {
    return {
        quoteNumber:    draft.quoteNumber?.trim() || null,
        status:         draft.status,
        title:          draft.title,
        description:    draft.description,
        quotedAt:       draft.quotedAt   || null,
        expiryDate:     draft.expiryDate || null,
        isVatInclusive: !!draft.isVatInclusive,
        discountType:   Number(draft.discountType) || 0,
        discountValue:  Number(draft.discountValue) || 0,
        siteAddress1:   draft.siteAddress1?.trim()  || null,
        siteAddress2:   draft.siteAddress2?.trim()  || null,
        siteTownCity:   draft.siteTownCity?.trim()  || null,
        sitePostcode:   draft.sitePostcode?.trim()  || null,
        notes:          draft.notes?.trim()         || null,
        internalNotes:  draft.internalNotes?.trim() || null,
    };
}

export default function QuoteDetailsCard({ quote, dirtyRef, flushPendingRefresh, quotePdfEnabled = true }) {
    const updateQuote = useUpdateQuote();
    const isConverted = quote?.convertedToJobId != null;
    const locked = isConverted || quote?.status === 'Sent' || quote?.status === 'Accepted' || quote?.status === 'Rejected';

    const [draft, setDraft] = useState(() => toDraft(quote));
    const [saveState, setSaveState] = useState('idle');
    const savedFadeTimerRef = useRef(null);

    const lastSavedRef = useRef(toDraft(quote));
    const seedIdRef = useRef(quote?.id ?? null);
    const draftRef = useRef(draft);
    useEffect(() => { draftRef.current = draft; });

    // Seed the draft once per quote id. Mirrors customerDetailsCard.
    useEffect(() => {
        if (quote?.id !== seedIdRef.current) {
            const fresh = toDraft(quote);
            setDraft(fresh);
            lastSavedRef.current = fresh;
            seedIdRef.current = quote?.id ?? null;
            if (dirtyRef) dirtyRef.current = false;
            setSaveState('idle');
        }
    }, [quote, dirtyRef]);

    // Reconcile server → draft when not dirty (another tab edited).
    useEffect(() => {
        if (!dirtyRef?.current && quote) {
            const fresh = toDraft(quote);
            if (!draftsEqual(fresh, lastSavedRef.current)) {
                setDraft(fresh);
                lastSavedRef.current = fresh;
            }
        }
    }, [quote, dirtyRef]);

    // Solo users have no customer-facing email/accept/decline flow — they
    // mark quotes Accepted / Rejected manually via the Status dropdown
    // rendered above. So `locked` (Sent/Accepted/Rejected) shouldn't block
    // writes for them. Pro/Business still get the lock because the
    // customer-link click is the canonical path. `isConverted` is always
    // read-only regardless of tier.
    const saveBlocked = isConverted || (locked && quotePdfEnabled);

    const saveRef = useRef(null);
    useEffect(() => {
        saveRef.current = () => {
            if (saveBlocked) return;
            const id = seedIdRef.current;
            if (id == null) return;
            const body = draftRef.current;
            if (draftsEqual(body, lastSavedRef.current)) {
                if (dirtyRef) dirtyRef.current = false;
                setSaveState('idle');
                return;
            }
            setSaveState('saving');
            updateQuote.mutate({ id, body: toBody(body) }, {
                onSuccess: (fresh) => {
                    lastSavedRef.current = toDraft(fresh);
                    if (!draftsEqual(draftRef.current, lastSavedRef.current)) {
                        saveRef.current();
                    } else {
                        if (dirtyRef) dirtyRef.current = false;
                        setSaveState('saved');
                        flushPendingRefresh?.();
                        clearTimeout(savedFadeTimerRef.current);
                        savedFadeTimerRef.current = setTimeout(
                            () => setSaveState('idle'), 1500);
                    }
                },
                onError: () => setSaveState('error'),
            });
        };
    });

    const debouncedSave = useMemo(
        () => debounce(() => saveRef.current?.(), 600),
        []
    );

    useEffect(() => {
        const onBeforeUnload = () => { debouncedSave.flush(); };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            debouncedSave.flush();
            clearTimeout(savedFadeTimerRef.current);
        };
    }, [debouncedSave]);

    const handleChange = (field, value) => {
        setDraft(prev => ({ ...prev, [field]: value }));
        if (dirtyRef) dirtyRef.current = true;
        debouncedSave();
    };

    return (
        <div className="surface">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="fw-semibold">Details</span>
                {quote?.status === 'Draft' && (
                    <StatusPill state={saveState} onRetry={() => saveRef.current?.()} />
                )}
            </div>

            {isConverted && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-3" role="alert">
                    <BiLockAlt />
                    <span>
                        This quote has been converted to{' '}
                        <Link to={`/jobs/${quote.convertedToJobId}`}>job #{quote.convertedToJobId}</Link>
                        . It's read-only — use <strong>Duplicate</strong> to start a new one from this.
                    </span>
                </div>
            )}

            <Form noValidate>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Quote Number</Label>
                            <Input
                                value={draft.quoteNumber}
                                onChange={(e) => handleChange('quoteNumber', e.target.value)}
                                disabled={locked}
                            />
                        </FormGroup>
                    </Col>
                    {!quotePdfEnabled && (
                        <Col md={6}>
                            <FormGroup>
                                <Label>Status</Label>
                                <Input type="select"
                                       value={draft.status}
                                       onChange={(e) => handleChange('status', e.target.value)}
                                       disabled={isConverted}>
                                    {QUOTE_STATUS_EDIT_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value} disabled={o.isDisabled}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    )}
                </Row>

                <FormGroup>
                    <Label>Title</Label>
                    <Input
                        value={draft.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        disabled={locked}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Description</Label>
                    <Input type="textarea" rows={3}
                        value={draft.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        disabled={locked}
                    />
                </FormGroup>

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Quoted On</Label>
                            <DatePicker
                                value={draft.quotedAt}
                                handleChange={(v) => handleChange('quotedAt', v)}
                                disabled={locked}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Expires On</Label>
                            <DatePicker
                                value={draft.expiryDate}
                                handleChange={(v) => handleChange('expiryDate', v)}
                                disabled={locked}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                {/* VAT toggle — off means TaxAmount = 0 and the VAT row is
                    hidden on the PDF (replaced with "Not applicable"). */}
                <FormGroup switch className="mb-3">
                    <Input type="switch"
                        checked={!!draft.isVatInclusive}
                        onChange={(e) => handleChange('isVatInclusive', e.target.checked)}
                        disabled={locked}
                    />
                    <Label check className="ms-2">VAT-inclusive (20%)</Label>
                </FormGroup>

                <Row>
                    <Col md={draft.discountType == 0 ? 12 : 6}>
                        <FormGroup>
                            <Label>Discount</Label>
                            <Input type="select"
                                value={draft.discountType}
                                onChange={(e) => handleChange('discountType', Number(e.target.value))}
                                disabled={locked}
                            >
                                {DISCOUNT_TYPES.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    {draft.discountType != 0 && (
                        <Col md={6}>
                            <FormGroup>
                                <Label>
                                    {draft.discountType == 2
                                        ? 'Percent (%)'
                                        : `Amount (${currencySymbol(quote?.customerCurrency) || '£'})`}
                                </Label>
                                <Input type="number" min={0} step="0.01"
                                    value={draft.discountValue}
                                    onChange={(e) => handleChange('discountValue', e.target.value)}
                                    disabled={locked}
                                />
                            </FormGroup>
                        </Col>
                    )}
                </Row>

                <FormGroup>
                    <Label>Site Address Line 1</Label>
                    <Input
                        value={draft.siteAddress1}
                        onChange={(e) => handleChange('siteAddress1', e.target.value)}
                        disabled={locked}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Site Address Line 2</Label>
                    <Input
                        value={draft.siteAddress2}
                        onChange={(e) => handleChange('siteAddress2', e.target.value)}
                        disabled={locked}
                    />
                </FormGroup>
                <Row>
                    <Col md={8}>
                        <FormGroup>
                            <Label>Town / City</Label>
                            <Input
                                value={draft.siteTownCity}
                                onChange={(e) => handleChange('siteTownCity', e.target.value)}
                                disabled={locked}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Postcode</Label>
                            <Input
                                value={draft.sitePostcode}
                                onChange={(e) => handleChange('sitePostcode', e.target.value)}
                                disabled={locked}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <FormGroup>
                    <Label>Notes — printed on the PDF for the customer</Label>
                    <Input type="textarea" rows={3}
                        value={draft.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        disabled={locked}
                    />
                </FormGroup>

                <FormGroup>
                    <Label className="d-inline-flex align-items-center gap-1">
                        <BiLockAlt />
                        Internal notes — never shown to the customer or on the PDF
                    </Label>
                    <Input type="textarea" rows={3}
                        value={draft.internalNotes}
                        onChange={(e) => handleChange('internalNotes', e.target.value)}
                        disabled={locked}
                    />
                </FormGroup>
            </Form>
        </div>
    );
}

function StatusPill({ state, onRetry }) {
    if (state === 'idle') return null;
    if (state === 'saving') {
        return (
            <span className="d-inline-flex align-items-center gap-1 text-muted small">
                <Spinner size="sm" /> Saving…
            </span>
        );
    }
    if (state === 'saved') {
        return (
            <span className="d-inline-flex align-items-center gap-1 text-success small">
                <BiCheckCircle /> Saved
            </span>
        );
    }
    return (
        <span className="d-inline-flex align-items-center gap-1 text-danger small">
            <BiErrorCircle /> Save failed
            <Button size="sm" color="link" className="p-0 ms-1" onClick={onRetry}>retry</Button>
        </span>
    );
}
